import React, { useState, useContext } from 'react';

import { ClickAwayListener } from '@material-ui/core';
import { setFiltersAction, setTeamAction } from 'actions/globalFilters';
import { tasksFilterViewUpdateAction } from 'actions/tasksFilters';
import { batch, useDispatch, useSelector, shallowEqual } from 'react-redux';
import { selectMeshListGlobalFilters } from 'selectors/globalFilters';
import styled from 'styled-components';
import { FlexContainer, StyledButton, StyledDivider, StyledTypography } from 'styles/common';

import { getCapitalizedTeams } from 'constants/helper';

import MeshListContext from '../MeshListContext';

const Wrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const FooterWrapper = styled.div`
  padding: 1rem 1.5rem;
  display: flex;
`;

const ContentWrapper = styled.div`
  flex-grow: 1;
`;

const CustomStyledButton = styled(StyledButton)`
  &.MuiButton-root.Mui-disabled {
    color: white;
  }
`;

export const ButtonGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 2vh 5%;
  padding: 1rem;
`;

const SelectTeamGrid = ({ employee, isDisabled = false, teamFilter: selectedTeamIds = null, setTeamFilter }) => {
  const teams = getCapitalizedTeams(employee.teams);

  const toggleTeamSelection = (teamId) => {
    if (selectedTeamIds === null) {
      setTeamFilter(teamId);
      return;
    }
    const teamsArray = selectedTeamIds?.split(',') || [];

    if (teamsArray.includes(teamId)) {
      if (teamsArray.length === 1) {
        // last and only team id is unselected
        setTeamFilter(null);
        return;
      }
      setTeamFilter(teamsArray.filter((val) => val !== teamId).join());
    } else {
      setTeamFilter([...teamsArray, teamId].join());
    }
  };

  return (
    <ButtonGrid>
      {teams.map((team) => (
        <StyledButton
          elongated
          variant="outlined"
          padding="0.15rem 0.5rem"
          color={selectedTeamIds?.split(',').includes(team.id) ? 'primary' : 'textSecondary'}
          onClick={() => toggleTeamSelection(team.id)}
        >
          {team.name}
        </StyledButton>
      ))}
    </ButtonGrid>
  );
};

const SelectStatusGrid = ({ statusFilter = [], setStatusFilter }) => {
  const taskStatusFilters = [
    {
      key: 'COMPLETED',
      label: 'Completed',
      isActive: statusFilter.includes('COMPLETED'),
    },
    {
      key: 'ONGOING',
      label: 'Ongoing',
      isActive: statusFilter.includes('ONGOING'),
    },
    {
      key: 'DELAYED',
      label: 'Delayed',
      isActive: statusFilter.includes('DELAYED'),
    },
    {
      key: 'UNPLANNED',
      label: 'Unplanned',
      isActive: statusFilter.includes('UNPLANNED'),
    },
  ];

  const toggleFilterSelection = (statusValue) => {
    if (statusFilter.includes(statusValue)) {
      setStatusFilter(statusFilter.filter((val) => val !== statusValue));
    } else {
      setStatusFilter([...statusFilter, statusValue]);
    }
  };

  return (
    <ButtonGrid>
      {taskStatusFilters.map(({ key, label, isActive }) => (
        <StyledButton
          elongated
          key={key}
          variant="outlined"
          color={isActive ? 'primary' : 'textSecondary'}
          onClick={() => toggleFilterSelection(key)}
        >
          {label}
        </StyledButton>
      ))}
    </ButtonGrid>
  );
};

const TasksFilterPanel = ({ employee, forceUpdateParent }) => {
  const { dispatch, tasksFilters } = useContext(MeshListContext);
  const globalFilters = useSelector(selectMeshListGlobalFilters, shallowEqual);
  const existingStatusFilter = globalFilters.filters
    .filter((filter) => filter.type === 'STATUS')
    .map((filter) => filter.value);

  const reduxDispatch = useDispatch();
  const [statusFilter, setStatusFilter] = useState(existingStatusFilter);
  const [teamFilter, setTeamFilter] = useState(globalFilters.teamId);

  const getStatusFilters = () => statusFilter.map((filter) => ({ type: 'STATUS', value: filter, operator: 'IN' }));

  const onApply = () => {
    batch(() => {
      reduxDispatch(setFiltersAction(statusFilter.length > 0 ? getStatusFilters() : [], 'meshList'));
      reduxDispatch(setTeamAction(teamFilter, 'meshList'));

      // dispatch(tasksUpdateTeamSelectionAction(teamFilter));
      // dispatch(tasksUpdateStatusSelectionAction(statusFilter));
      dispatch(tasksFilterViewUpdateAction(false));
      forceUpdateParent();
    });
  };

  return (
    <ClickAwayListener onClickAway={() => dispatch(tasksFilterViewUpdateAction(false))}>
      <Wrapper>
        <ContentWrapper>
          <FlexContainer leftGutter={2} topGutter={2}>
            <StyledTypography weight={600}>Team</StyledTypography>
          </FlexContainer>
          <SelectTeamGrid employee={employee} teamFilter={teamFilter} setTeamFilter={setTeamFilter} />
          <StyledDivider variant="fullWidth" bottomGutter={2} />

          <FlexContainer leftGutter={2}>
            <StyledTypography weight={600}>Status</StyledTypography>
          </FlexContainer>

          <SelectStatusGrid statusFilter={statusFilter} setStatusFilter={setStatusFilter} />
        </ContentWrapper>

        <StyledDivider variant="fullWidth" />

        <FooterWrapper>
          <CustomStyledButton
            variant="outlined"
            customColor="primary"
            fontColor="white"
            onClick={onApply}
            borderColor="primary"
            elongated
          >
            Apply
          </CustomStyledButton>
          <StyledButton
            variant="outlined"
            customColor="white"
            fontColor="primary"
            onClick={() => dispatch(tasksFilterViewUpdateAction(false))}
            disabled={false}
            leftGutter={2}
            borderColor="primary"
            elongated
          >
            Cancel
          </StyledButton>
        </FooterWrapper>
      </Wrapper>
    </ClickAwayListener>
  );
};

export default TasksFilterPanel;
